import { Image } from 'expo-image';
import { View } from 'react-native';

import {
  ActionSheet,
  Button,
  IconClose,
  Pressable,
  Text,
  createMqStyles,
  useMqSelect,
} from '@fhs/ui';

import { useComboSelectionContext } from '../../state/combo-selection';
import { useMenuPicker } from '../../state/menu-picker';
import { formatCentsToDollars } from '../../utils';
import { PICKER_SELECTION_TYPE } from '../guide/constants';

const ActionButtons = ({
  onClose,
  setIsComboGuideOpen,
  setIsComboEnabled,
  setShouldAddToCartDirectly,
  onCancel,
  setPickerSelectionType,
}: {
  onClose: () => void;
  onCancel: () => void;
  setIsComboGuideOpen: (value: boolean) => void;
  setIsComboEnabled: (enabled: boolean) => void;
  setShouldAddToCartDirectly: (value: boolean) => void;
  setPickerSelectionType: (type: string) => void;
}) => {
  return (
    <>
      <Button
        size="xl"
        style={{ flex: 1 }}
        onPress={() => {
          onClose();

          setIsComboEnabled(true);
          setPickerSelectionType(PICKER_SELECTION_TYPE.COMBO);
          setShouldAddToCartDirectly(true);

          // This is a hack to solve a race condition between the interaction of two different action sheets on iOS
          // The first should close and the second should open, having this will avoid the issue since we defer the opening action to the next event loop tick
          setTimeout(() => {
            setIsComboGuideOpen(true);
          }, 0);
        }}
      >
        <Button.Text>Make it a Combo</Button.Text>
      </Button>
      <Button
        size="xl"
        type="outline"
        style={{ flex: 1 }}
        onPress={() => {
          onClose();
          onCancel();
        }}
      >
        <Button.Text>No, Thanks</Button.Text>
      </Button>
    </>
  );
};

export const MakeComboActionSheet = ({
  isOpen,
  onClose,
  menuPicker,
  onCancel,
}: {
  isOpen: boolean;
  onClose: () => void;
  menuPicker: ReturnType<typeof useMenuPicker>;
  onCancel: () => void;
}) => {
  const mqStyles = useMqStyles();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const {
    setIsComboGuideOpen,
    setIsComboEnabled,
    setPickerSelectionType,
    setShouldAddToCartDirectly,
  } = useComboSelectionContext();

  return (
    menuPicker.query.data?.combo && (
      <ActionSheet
        onRequestClose={onClose}
        onClose={onClose}
        isVisible={isOpen}
        noPanGestureDetection
      >
        <View style={mqStyles.container}>
          {isDesktop ? (
            <View style={mqStyles.closeIconWrapper}>
              <Pressable onPress={onClose}>
                <IconClose size={32} />
              </Pressable>
            </View>
          ) : null}

          <Image
            source={require('./combo.webp')}
            alt="Make it a Combo"
            placeholder={{ thumbhash: 'bykOJIL4lrhqmXV+hZiPdIJICQ' }}
            contentFit="contain"
            style={{ width: '70%', aspectRatio: 280 / 164 }}
          />
          <Text.Heading type="one" style={{ marginTop: 20 }}>
            Make it a Combo
          </Text.Heading>
          <Text.Paragraph
            size="md"
            style={{ width: '70%', textAlign: 'center', marginVertical: 20 }}
          >
            {menuPicker.query.data?.combo.description} for only{' '}
            <Text weight="semibold">
              {formatCentsToDollars(menuPicker.query.data?.combo.basePriceCents)}
            </Text>
          </Text.Paragraph>

          <View style={mqStyles.actionButtons}>
            <ActionButtons
              onClose={onClose}
              onCancel={onCancel}
              setIsComboEnabled={setIsComboEnabled} // TODO: remove/handle this on FHS-4323
              setIsComboGuideOpen={setIsComboGuideOpen}
              setPickerSelectionType={setPickerSelectionType}
              setShouldAddToCartDirectly={setShouldAddToCartDirectly}
            />
          </View>
        </View>
      </ActionSheet>
    )
  );
};

const useMqStyles = createMqStyles({
  container: {
    $base: {
      alignItems: 'center',
      padding: 30,
    },
  },
  closeIconWrapper: {
    $gteDesktop: {
      position: 'absolute',
      top: 28,
      right: 28,
      alignSelf: 'flex-end',
      marginBottom: 8,
    },
  },
  actionButtons: {
    $base: {
      width: '100%',
      gap: 12,
      marginTop: 20,
    },
    $gteDesktop: {
      flexDirection: 'row-reverse',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: 16,
    },
  },
});
